import React from 'react';
import { ChevronRight, ExternalLink } from 'lucide-react';
import useBoolean from 'src/shared/hooks/useBoolean';
import Breadcrumbs, { Breadcrumb } from 'src/shared/components/breadcrumbs/Breadcrumbs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/shared/components/ui/dialog';
import { Button } from 'src/shared/components/ui/button';
import HubSpotForm, { type HubSpotFormProps } from 'src/shared/components/hubspot-form/HubSpotForm';
import ProductVisibilityNotice from 'src/shared/components/product-visibility-notice/ProductVisibilityNotice';
import { ProductKey } from 'src/shared/types/engine/products';

export type UpgradePageFeature = {
    icon: React.ComponentType<{ className?: string; size?: number }>;
    title: string;
    body: string;
};

export type UpgradePageWebinar = {
    label: string;
    href: string;
};

export type UpgradePageProps = {
    testId: string;
    breadcrumbs: Breadcrumb[];
    heroImage: string;
    /**
     * 'image-right' (default) confines the photo to the right of the hero with a solid dark
     * backing behind the text, matching the current Figma template. 'full-bleed' covers the
     * whole hero with the photo, for pages designed with an image composed for that (e.g. GNR).
     */
    heroLayout?: 'image-right' | 'full-bleed';
    badgeLabel: string;
    title: React.ReactNode;
    description: string;
    features: UpgradePageFeature[];
    ctaTitle: string;
    ctaDescription: string;
    webinar?: UpgradePageWebinar;
    onBookCallClick?: () => void;
    /** The HubSpot form to embed in the Book a call dialog. Every page has its own form, so there is no default. */
    hubSpotForm: HubSpotFormProps;
    /**
     * The product this page pitches. The page then tells internal staff its
     * setting, as `StandardAppLayout` does on the product's own pages.
     */
    product?: ProductKey;
};

const UpgradePage = ({
    testId,
    breadcrumbs,
    heroImage,
    heroLayout = 'image-right',
    badgeLabel,
    title,
    description,
    features,
    ctaTitle,
    ctaDescription,
    webinar,
    onBookCallClick,
    hubSpotForm,
    product,
}: UpgradePageProps) => {
    const [showBookCallModal, setShowBookCallModal] = useBoolean(false);

    const handleBookCallClick = () => {
        onBookCallClick?.();
        setShowBookCallModal.on();
    };

    return (
        <>
            <div className='flex flex-col w-full min-h-full overflow-auto' data-testid={testId}>
                {product && <ProductVisibilityNotice product={product} />}
                {/* Hero */}
                <div className='relative flex flex-col gap-4 h-[500px] items-stretch justify-between p-8 border-b border-neutral-90 shrink-0'>
                    {/* Background layers */}
                    {heroLayout === 'full-bleed' ? (
                        <div aria-hidden className='absolute inset-0 pointer-events-none'>
                            <div className='absolute inset-0 bg-aqua-95' />
                            <img src={heroImage} alt='' className='absolute inset-0 w-full h-full object-cover' />
                            <div className='absolute inset-0 bg-gradient-to-r from-black/30 to-transparent' />
                        </div>
                    ) : (
                        <div aria-hidden className='absolute inset-0 pointer-events-none overflow-hidden bg-black'>
                            <img src={heroImage} alt='' className='absolute inset-y-0 right-0 w-1/2 h-full object-cover' />
                            {/* Solid black past the photo boundary (50%) avoids a visible seam against the photo's own dark backdrop, then fades out further right so the photo shows through. */}
                            <div className='absolute inset-0 bg-[linear-gradient(to_right,#000_0%,#000_50%,rgba(0,0,0,0.15)_100%)]' />
                        </div>
                    )}

                    <div className='relative flex items-start justify-between w-full'>
                        <Breadcrumbs
                            breadcrumbs={breadcrumbs}
                            className='[&_span[data-testid=breadcrumb]_span]:text-grey-0 [&_span[data-testid=breadcrumb]:not(:last-child)_span]:text-neutral-40 [&_span[data-testid=breadcrumb]:not(:last-child)_span]:font-normal [&_svg]:text-neutral-40'
                        />
                        <Button className='bg-teal-30 text-black hover:bg-teal-30/90' onClick={handleBookCallClick}>
                            Book a call
                            <ChevronRight />
                        </Button>
                    </div>

                    <div className='relative flex flex-col gap-6 max-w-[750px] p-5'>
                        <div className='inline-flex items-center border border-neutral-90 bg-white/10 rounded-full px-3 py-0.5 w-fit'>
                            <span className='text-sm font-medium text-teal-30'>{badgeLabel}</span>
                        </div>
                        <h1 className='text-5xl leading-none font-normal text-grey-0 m-0 text-balance'>{title}</h1>
                        <p className='text-xl leading-6 font-normal tracking-tight text-neutral-40 m-0'>{description}</p>
                        <div className='flex items-center gap-2'>
                            <Button className='bg-teal-30 text-black hover:bg-teal-30/90' onClick={handleBookCallClick}>
                                Book a call
                                <ChevronRight />
                            </Button>
                            {webinar && (
                                <Button
                                    variant='outline'
                                    className='bg-white/10 border-neutral-90 text-grey-0 hover:bg-white/20 hover:text-grey-0 hover:border-neutral-90'
                                    asChild
                                >
                                    <a href={webinar.href} target='_blank' rel='noopener noreferrer'>
                                        {webinar.label}
                                        <ExternalLink />
                                    </a>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Features grid */}
                <div className='flex flex-col gap-4 p-8 border-b border-muted bg-white'>
                    <div className='grid grid-cols-3 gap-3'>
                        {features.map(({ icon: Icon, title: featureTitle, body }) => (
                            <div key={featureTitle} className='flex gap-4 items-start p-4 rounded-lg bg-white'>
                                <div className='shrink-0 bg-grey-0 p-2 rounded-lg'>
                                    <Icon size={24} className='text-primary' />
                                </div>
                                <div className='flex flex-col gap-2'>
                                    <p className='text-base font-medium text-primary m-0 leading-6'>{featureTitle}</p>
                                    <p className='text-sm text-muted-foreground m-0 leading-5'>{body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className='p-8 bg-white'>
                    <div className='flex items-center justify-between p-4 rounded-lg'>
                        <div className='flex flex-col gap-2 flex-1 max-w-[700px]'>
                            <h2 className='text-2xl font-normal  text-black m-0 leading-7'>{ctaTitle}</h2>
                            <p className='text-base text-muted-foreground m-0 leading-6'>{ctaDescription}</p>
                        </div>
                        <Button className='shrink-0' onClick={handleBookCallClick}>
                            Book a call
                            <ChevronRight />
                        </Button>
                    </div>
                </div>
            </div>

            <Dialog open={showBookCallModal} onOpenChange={open => !open && setShowBookCallModal.off()}>
                <DialogContent className='w-[625px] max-w-[625px] max-h-[90vh]'>
                    <DialogHeader>
                        <DialogTitle className='text-primary text-xl font-normal'>Book a call</DialogTitle>
                    </DialogHeader>
                    <div className='max-h-[70vh] overflow-y-auto'>
                        <HubSpotForm {...hubSpotForm} />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default UpgradePage;
